/**
 * Ecranele de stare: incarcare, eroare, gol.
 *
 * Regula pe care o impun: o eroare de la server se ARATA. Nu exista cale prin
 * care un raspuns nereusit sa devina o pagina goala cu "nu sunt rezultate" —
 * clientul ar crede ca restaurantul nu are meniu, iar noi nu am afla niciodata.
 *
 * Textul afisat e mesajul serverului, care e in romana si stie ce s-a intamplat.
 * Ce adaugam noi nu presupune nicio cauza.
 *
 * Ce NU se mai afiseaza: codul intern de eroare, statusul HTTP si ruta. Clientul
 * nu poate face nimic cu ele, iar ecranul devine un raport de defect. Detaliul
 * tehnic se scrie in consola (`client.ts`), iar pe ecran ramane referinta
 * cererii — singurul lucru pe care asistenta chiar il cauta in loguri.
 */

import type { ReactNode } from "react";

import { CORE_ERROR_CODES } from "@deliveryapp/shared";

import { ApiError, CLIENT_ERROR_CODES } from "../api/client";
import { Alert, Button, Spinner } from "./ui";

/**
 * Completari factuale, doar unde mesajul serverului nu poate exista. Fiecare
 * spune ce s-a intamplat, niciuna nu spune DE CE: "piata nu e configurata" e o
 * cauza pe care ecranul nu are cum s-o stie, iar cine o citeste porneste sa
 * repare configurarea in loc sa se uite la ce a raspuns serverul.
 */
const CODE_NOTES: Record<string, string> = {
  [CORE_ERROR_CODES.MARKET_NOT_FOUND]: "Serverul nu a recunoscut piața cerută de aplicație.",
  [CORE_ERROR_CODES.ROUTE_NOT_FOUND]: "Serverul nu recunoaște ruta cerută de aplicație.",
  [CORE_ERROR_CODES.UPSTREAM_POS_UNAVAILABLE]:
    "Sistemul restaurantului nu a răspuns la această cerere.",
  [CLIENT_ERROR_CODES.NETWORK]: "Cererea nu a ajuns la server.",
  [CLIENT_ERROR_CODES.TIMEOUT]: "Serverul nu a răspuns în intervalul alocat.",
  [CLIENT_ERROR_CODES.MALFORMED_RESPONSE]:
    "Răspunsul primit nu are forma pe care o așteaptă aplicația.",
  [CLIENT_ERROR_CODES.GATEWAY_UNAVAILABLE]: "Serverul nu a răspuns la această cerere.",
};

export function LoadingState({ label }: { label: string }) {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-ink-muted" role="status">
      <Spinner size={28} />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  title = "Nu am putut încărca informația",
}: {
  error: ApiError;
  onRetry?: () => void;
  title?: string;
}) {
  const note = CODE_NOTES[error.code];
  return (
    <div className="mx-auto max-w-readable py-8">
      <Alert
        tone="error"
        title={title}
        actions={
          <>
            {error.requiresReload ? (
              <Button onClick={() => window.location.reload()}>Reîncarcă aplicația</Button>
            ) : null}
            {onRetry !== undefined && error.retryable && !error.requiresReload ? (
              <Button onClick={onRetry}>Încearcă din nou</Button>
            ) : null}
          </>
        }
      >
        <p>{error.message}</p>
        {note === undefined ? null : <p className="mt-1 text-ink-muted">{note}</p>}
        {error.requestId === undefined ? null : (
          <p className="mt-2 text-xs text-ink-muted">
            {`Referință pentru asistență: ${error.requestId}`}
          </p>
        )}
      </Alert>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mx-auto max-w-readable rounded-card border border-line bg-surface px-6 py-10 text-center">
      <p className="text-lg font-semibold text-ink">{title}</p>
      {description === undefined ? null : <p className="mt-2 text-sm text-ink-muted">{description}</p>}
      {action === undefined ? null : <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}
